import { useEffect, useRef, useState } from "react";
import Cropper, { ReactCropperElement } from "react-cropper";
import Tabs from "../Tabs";
import FaQuestion from "@/components/SVG/Fontawesome/FaQuestion";
import FaGallery from "@/components/SVG/Fontawesome/FaGallery";
import { browserName, fullBrowserVersion, deviceType, isFirefox } from "react-device-detect";
import { getImageUploadUrl, getMatches, uploadQuestionImage } from "@/helpers/api/service/ask";
import { getUUID } from "@/utils/auth";
import { useDispatch, useSelector } from "react-redux";
import { selectAskQuestionId, selectCameraBlob, updateOffCanvasViewPayload } from "@/store/slices/offCanvas";
import Compressor from "compressorjs";
import useZoom from "@/hooks/useZoom";
import FaCameraReset from "@/components/SVG/Fontawesome/FaCameraReset";
import dynamic from "next/dynamic";
import VideoFeed from "./VideoFeed";

const ProcressingLoader = dynamic(() => import("../ProcessingLoader"));

import "cropperjs/dist/cropper.css";

const ImageSearch = () => {
  const dispatch = useDispatch();
  const blob = useSelector(selectCameraBlob);
  const questionId = useSelector(selectAskQuestionId);

  const zoom = useZoom();

  const [cameraBlocked, setCameraBlocked] = useState<boolean>(false);
  const [processing, setProcessing] = useState<boolean>(false);

  const mediaTrackRef = useRef<MediaStreamTrack | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const cropperRef = useRef<ReactCropperElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);

  const compressImage = (blob: Blob) => {
    new Compressor(blob, {
      quality: 0.6,
      mimeType: "image/png",
      maxWidth: window.innerWidth * window.devicePixelRatio,
      maxHeight: window.innerHeight * window.devicePixelRatio,
      success: (compressedBlob) => {
        setProcessing(false);
        dispatch(updateOffCanvasViewPayload({ CAMERA: { blob: URL.createObjectURL(compressedBlob) } }));
        stopVideo();
      },
      error: (e) => {
        setProcessing(false);
        window.dataLayer?.push({
          event: "image_compression_error",
          image_compression_error: {
            browserName,
            fullBrowserVersion,
            deviceType,
          },
        });
      },
    });
  }

  const captureImage = async () => {
    if (cameraBlocked) return;
    try {
      setProcessing(true);
      if (!mediaTrackRef.current) throw new Error("MediaTrack not found");
      const imageCapture = new window.ImageCapture(mediaTrackRef.current);
      const blob = await imageCapture.takePhoto();
      compressImage(blob);
    } catch (e) {
      setProcessing(false);
      window.dataLayer?.push({
        event: "image_capture_error",
        image_capture_error: {
          browserName,
          fullBrowserVersion,
          deviceType,
        },
      });
    }
  }

  const canvasToBlob = (canvas: HTMLCanvasElement): Promise<Blob | null> => {
    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), "image/png", 1);
    });
  }

  const pickFile = (e: any) => {
    if (!e.target.files?.length) return;
    // const reader = new FileReader();
    // reader.addEventListener("load", () => {
    //   setCameraImage(new Blob([reader.result as ArrayBuffer], { type: "image/png" }));
    // });
    // reader.readAsDataURL(e.target.files[0]);
    compressImage(e.target.files[0]);
  }

  const uploadImage = async () => {
    if (!cropperRef.current) return;
    setProcessing(true);
    try {
      const blobImg = await canvasToBlob(cropperRef.current?.cropper.getCroppedCanvas());
      const res = await getImageUploadUrl(getUUID());
      const uploadUrl = res?.data?.url;

      await uploadQuestionImage(uploadUrl, blobImg);

      const askRes = await getMatches(getUUID(), res?.data?.file_name, res?.data?.question_id);
      dispatch(updateOffCanvasViewPayload({
        CAMERA: {
          img: askRes?.data?.question_image ?? "",
          questionId: res?.data?.question_id,
          matchedQuestions: askRes?.data?.matched_questions ?? [],
        },
      }));
      setProcessing(false);
    } catch {
      setProcessing(false);
    }
  }

  const startVideo = async () => {
    if (!videoRef.current) return;
    try {
      navigator.mediaDevices.getUserMedia = navigator.mediaDevices.getUserMedia || (navigator as any).webkitGetUserMedia || (navigator as any).mozGetUserMedia || (navigator as any).msGetUserMedia;
      const mediaStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment", aspectRatio: 1 } });
      videoRef.current.srcObject = mediaStream;
      mediaTrackRef.current = mediaStream.getVideoTracks()[0];
    } catch (e) {
      window.dataLayer?.push({
        event: "camera_blocked",
        camera_blocked: {
          browserName,
          fullBrowserVersion,
          deviceType,
        },
      });
      setCameraBlocked(true);
    }
  }

  const stopVideo = () => {
    mediaTrackRef.current?.stop();
  }

  const resetState = () => {
    stopVideo();
    dispatch(updateOffCanvasViewPayload({ CAMERA: { blob: null } }));
    setProcessing(false);
  }

  const reCapture = () => {
    dispatch(updateOffCanvasViewPayload({ CAMERA: { blob: null } }));
    startVideo();
  }

  const openHelp = () => {
    dispatch(updateOffCanvasViewPayload({ CAMERA: { help: true } }));
  }

  useEffect(() => {
    mediaTrackRef.current?.applyConstraints({
      // @ts-ignore
      advanced: [{ zoom }],
    });
  }, [zoom]);

  useEffect(() => {
    if (questionId) return;
    startVideo();

    return () => {
      resetState();
    }
  }, [questionId]);

  if (!blob) {
    return (
      <>
        <VideoFeed videoRef={videoRef} mediaTrackRef={mediaTrackRef} isBlocked={cameraBlocked} />
        <div className="bg-black flex flex-col py-2">
          <Tabs />
          <div className="grid grid-cols-3 w-full h-32 justify-items-center place-items-center">
            <span className="flex flex-col justify-center items-center text-white text-xs gap-1">
              <button onClick={openHelp} className="text-xl border rounded-full px-2 py-1 flex justify-center items-center">
                <FaQuestion />
              </button>
              <span>Help</span>
            </span>
            <button onClick={captureImage} className="bg-red-600 focus:bg-red-700 border-4 h-16 w-16 rounded-full p-2 disabled:bg-gray-500" disabled={cameraBlocked}>
            </button>
            <div className="flex flex-col justify-center items-center text-white text-xs gap-0.5">
              <label htmlFor="gallery-input">
                <FaGallery className="fill-white" />
              </label>
              <input ref={galleryRef} id="gallery-input" className="hidden" type="file" onChange={pickFile} accept="image/*" />
              <span>Gallery</span>
            </div>
          </div>
        </div>
        {processing && <ProcressingLoader />}
      </>
    )
  }

  return (
    <>
      <VideoFeed videoRef={videoRef} mediaTrackRef={mediaTrackRef} isBlocked={cameraBlocked} />
      <div className="w-full h-full grow flex flex-col justify-center items-center">
        <Cropper
          className="!max-h-[70vh] !max-w-[90vw]"
          src={blob}
          ref={cropperRef}
          initialAspectRatio={16 / 9}
          minCropBoxWidth={100}
          minCropBoxHeight={20}
          dragMode="move"
        />
      </div>
      <div className="bg-black flex flex-col py-2">
        <div className="grid grid-cols-3 w-full h-32 justify-items-center place-items-center">
          <button onClick={reCapture} className="flex flex-col justify-center items-center text-center gap-0.5">
            <FaCameraReset className="fill-white h-8 w-8" />
            <span className="text-xxs text-white">Retake</span>
          </button>
          <button onClick={uploadImage} className="col-span-2 btn-primary">Find Solution</button>
        </div>
      </div>
      {processing && <ProcressingLoader />}
    </>
  )
}

export default ImageSearch;